import { useEffect, useReducer, useState } from 'react';
import FIELD_ERROR_MESSAGE from './utils/errorMessages';
import { IFormState } from './types';
import { ACTIONS } from './actions';
import { scrollIntoViewWrapper } from './utils/scrollIntoView';
import { fetchAmount, postData } from './api';
import {
    constructPayload,
    mapAddonIdsForQuotePriceRequest,
} from './utils/helper';
import {
    getTotalPremiumInclGst,
    parseGetQuotePriceResponse,
} from './utils/quotePriceResponse';

export const useFormData = () => {
    const getAmountForVariant = async ({
        vehicle_type,
        variant_ekey,
        no_of_trailers,
        selected_addons,
    }) => {
        try {
            const data: any = await fetchAmount({
                vehicle_type,
                variant_ekey,
                no_of_trailers,
                addons: mapAddonIdsForQuotePriceRequest(selected_addons),
            });
            const { plan, variantId, addonsAvailable } =
                parseGetQuotePriceResponse(data);
            const displayTotal = getTotalPremiumInclGst(plan);
            dispatch({
                type: ACTIONS.SET_FORM_DATA,
                payload: {
                    key: 'quotePlan',
                    value: plan,
                },
            });
            dispatch({
                type: ACTIONS.SET_FORM_DATA,
                payload: {
                    key: 'addonsAvailable',
                    value: addonsAvailable,
                },
            });
            dispatch({
                type: ACTIONS.SET_FORM_DATA,
                payload: {
                    key: 'totalAmount',
                    value: displayTotal > 0 ? displayTotal : null,
                },
            });
            dispatch({
                type: ACTIONS.SET_FORM_DATA,
                payload: {
                    key: 'variant_id',
                    value: variantId,
                },
            });
        } catch (e) {
            console.log(e);
        }
    };

    const submitFormData = async () => {
        const payload = constructPayload(state);
        const response: any = await postData(payload);
        if (response.status === 202) {
            dispatch({
                type: ACTIONS.SET_FORM_SUBMITTED,
            });
        }
    };

    const initalErrorState = {
        name: FIELD_ERROR_MESSAGE.name.required,
        phoneNumber: FIELD_ERROR_MESSAGE.phone.required,
        email: FIELD_ERROR_MESSAGE.email.required,
        pincode: FIELD_ERROR_MESSAGE.pincode.required,
        address: FIELD_ERROR_MESSAGE.address.required,
        registrationNumber: 'Registration number is required',
        registrationYearMonth: 'Registration year and month are required',
        engineNumber: FIELD_ERROR_MESSAGE.engine.required,
        chassisNumber: FIELD_ERROR_MESSAGE.chassis.required,
        previousPolicyInsurer:
            FIELD_ERROR_MESSAGE.previousPolicyInsurer.required,
        // totalAmount: 'Total amount is required',
        make: FIELD_ERROR_MESSAGE?.make?.required,
        model: FIELD_ERROR_MESSAGE?.model?.required,
        variant: FIELD_ERROR_MESSAGE?.variant?.required,
    };

    const initalTrailerErrorState = {
        registrationNumber: 'Registration number is required',
        chassisNumber: FIELD_ERROR_MESSAGE.chassis.required,
        type: 'Trailer type is required',
    };

    const initalState: IFormState = {
        name: '',
        phoneNumber: '',
        email: '',
        pincode: '',
        address: '',
        registrationNumber: '',
        registrationYear: '',
        registrationMonth: '',
        engineNumber: '',
        chassisNumber: '',
        financierName: '',
        previousPolicyInsurer: '',
        totalAmount: null,
        isVehicleFinanced: { value: '0', displayValue: 'No' },
        isPreviousPolicyExpired: { value: '1', displayValue: 'Yes' },
        previousPolicyExpiryDate: new Date(
            new Date().setDate(new Date().getDate() - 1)
        ),
        policyStartDate: new Date(
            new Date(new Date().getTime() + 24 * 60 * 60 * 1000).setHours(
                0,
                0,
                0,
                0
            )
        ),
        claimedInPreviousPolicy: { value: '0', displayValue: 'No' },
        vehicleType: { value: 'pcv', displayValue: 'PCV' },
        make: {
            id: '',
            image: '',
            name: '',
            variants: [],
        },
        model: {
            name: '',
            value: '',
        },
        variant: {
            id: '',
            name: '',
            fuel_type: '', // eslint-disable-line
            cc: '',
        },
        numberOfTrailers: null,
        errors: initalErrorState,
        trailerInfoErrors: null,
        showError: false,
        variant_id: null,
        quotePlan: null,
        addonsAvailable: [],
        selectedAddons: [],
        formSubmitted: null,
        trailersInfo: null,
    };

    function reducer(state: IFormState, action) {
        if (action.type === ACTIONS.SET_FORM_DATA) {
            return {
                ...state,
                [action.payload.key]: action.payload.value,
            };
        }
        if (action.type === ACTIONS.RESET_FIELD) {
            return {
                ...state,
                [action.payload.key]: initalState[action.payload.key],
            };
        }
        if (action.type === ACTIONS.RESET_MMV) {
            return {
                ...state,
                make: initalState.make,
                model: initalState.model,
                variant: initalState.variant,
                totalAmount: initalState.totalAmount,
                variant_id: initalState.variant_id,
                quotePlan: initalState.quotePlan,
                addonsAvailable: initalState.addonsAvailable,
                selectedAddons: initalState.selectedAddons,
                errors: {
                    ...state.errors,
                    make: initalErrorState.make,
                    model: initalErrorState.model,
                    variant: initalErrorState.variant,
                },
            };
        }
        if (action.type === ACTIONS.SET_SHOW_ERROR) {
            return {
                ...state,
                showError: action.payload,
            };
        }
        if (action.type === ACTIONS.SET_ERRORS) {
            return {
                ...state,
                errors: {
                    ...state.errors,
                    [action.payload.key]: action.payload.value,
                },
            };
        }
        if (action.type === ACTIONS.DELETE_ERROR) {
            const tempState = {
                ...state,
            };
            delete tempState.errors[action.payload.key];
            return tempState;
        }
        if (action.type === ACTIONS.GET_AMOUNT) {
            getAmountForVariant({ ...action.payload });
        }
        if (action.type === ACTIONS.SET_FORM_SUBMITTED) {
            return {
                ...state,
                formSubmitted: true,
            };
        }
        if (action.type === ACTIONS.INITIATE_TRAILERS_INFO) {
            const trailersNumber = state.numberOfTrailers;
            const trailersInfo: any = [];
            const trailersInfoErrors: any = [];
            trailersNumber &&
                trailersNumber > 1 &&
                [...Array(trailersNumber - 1).keys()].forEach((t) => {
                    trailersInfo.push({
                        id: `trailer_${t}`,
                        registrationNumber: '',
                        chassisNumber: '',
                        type: { value: 'open', displayValue: 'Open' },
                        label: `Trailer ${t + 1} details`,
                    });
                    trailersInfoErrors.push({
                        id: `trailer_${t}`,
                        ...initalTrailerErrorState,
                    });
                });

            return {
                ...state,
                trailersInfo,
                trailerInfoErrors: trailersInfoErrors,
                errors: {
                    ...state.errors,
                    trailersInfo: 'Please fill all the trailers information',
                },
            };
        }
        if (action.type === ACTIONS.SET_TRAILERS_INFO) {
            const { id, key, value } = action.payload;
            let trailersInfo = state.trailersInfo
                ? [...state.trailersInfo]
                : null;
            trailersInfo =
                trailersInfo &&
                trailersInfo.map((t) => {
                    if (t?.id === id) {
                        return {
                            ...t,
                            [key]: value,
                        };
                    }
                    return t;
                });
            return {
                ...state,
                trailersInfo,
            };
        }
        if (action.type === ACTIONS.SET_TRAILER_INFO_ERROR) {
            const { key, value, id } = action.payload;
            let trailerInfoErrors = state.trailerInfoErrors
                ? [...state.trailerInfoErrors]
                : null;
            trailerInfoErrors =
                trailerInfoErrors &&
                trailerInfoErrors.map((trailerErrorObj) => {
                    if (id === trailerErrorObj?.id) {
                        return {
                            ...trailerErrorObj,
                            [key]: value,
                        };
                    }
                    return trailerErrorObj;
                });
            return {
                ...state,
                ...(trailerInfoErrors && {
                    trailerInfoErrors,
                }),
            };
        }
        if (action.type === ACTIONS.DELETE_TRAILER_INFO_ERROR) {
            const { key, id } = action.payload;
            let trailerInfoErrors = state.trailerInfoErrors
                ? [...state.trailerInfoErrors]
                : null;
            trailerInfoErrors =
                trailerInfoErrors &&
                trailerInfoErrors.map((trailerErrorObj) => {
                    if (id === trailerErrorObj?.id) {
                        delete trailerErrorObj?.[key];
                        return trailerErrorObj;
                    }
                    return trailerErrorObj;
                });
            return {
                ...state,
                ...(trailerInfoErrors && {
                    trailerInfoErrors,
                }),
            };
        }

        return {
            ...state,
        };
    }

    const [state, dispatch] = useReducer(reducer, initalState);

    const [shouldScrollIntoView, setShouldScrollIntoView] = useState(false);

    useEffect(() => {
        const { registrationYear, registrationMonth } = state;

        if (registrationYear === '' && registrationMonth === '') {
            dispatch({
                type: ACTIONS.SET_ERRORS,
                payload: {
                    key: 'registrationYearMonth',
                    value: 'Registration year and month are required',
                },
            });
        } else if (registrationYear && registrationMonth === '') {
            dispatch({
                type: ACTIONS.SET_ERRORS,
                payload: {
                    key: 'registrationYearMonth',
                    value: FIELD_ERROR_MESSAGE.regdYear.required,
                },
            });
        } else if (registrationMonth && registrationYear === '') {
            dispatch({
                type: ACTIONS.SET_ERRORS,
                payload: {
                    key: 'registrationYearMonth',
                    value: FIELD_ERROR_MESSAGE.regdMonth.required,
                },
            });
        } else {
            dispatch({
                type: ACTIONS.DELETE_ERROR,
                payload: {
                    key: 'registrationYearMonth',
                },
            });
        }
    }, [state.registrationYear, state.registrationMonth]);

    useEffect(() => {
        const { variant, numberOfTrailers, vehicleType } = state;
        if (variant?.id && vehicleType?.value) {
            if (
                (vehicleType?.value === 'tractor' &&
                    numberOfTrailers &&
                    numberOfTrailers >= 0) ||
                vehicleType?.value !== 'tractor'
            )
                dispatch({
                    type: ACTIONS.GET_AMOUNT,
                    payload: {
                        vehicle_type: state.vehicleType?.value,
                        variant_ekey: state.variant.id,
                        no_of_trailers: state.numberOfTrailers,
                        selected_addons: state.selectedAddons,
                    },
                });
        }
    }, [
        state?.variant?.id,
        state?.numberOfTrailers,
        state?.vehicleType?.value,
        state?.selectedAddons,
    ]);

    useEffect(() => {
        if (shouldScrollIntoView) {
            const errorKeys = Object.keys(state.errors);
            const toScrollId = errorKeys[0];

            scrollIntoViewWrapper(toScrollId);

            setShouldScrollIntoView(false);
        }
    }, [shouldScrollIntoView]);

    useEffect(() => {
        if (state.vehicleType?.value === 'tractor' && state.numberOfTrailers) {
            if (state.numberOfTrailers > 1) {
                dispatch({
                    type: ACTIONS.INITIATE_TRAILERS_INFO,
                });
            } else {
                dispatch({
                    type: ACTIONS.DELETE_ERROR,
                    payload: {
                        key: 'trailersInfo',
                    },
                });
            }
        }
        // Add reset logic if pcv, gcv is selected
    }, [state.vehicleType?.value, state.numberOfTrailers]);

    useEffect(() => {
        const { trailerInfoErrors } = state;
        let isTrailerInfoErrorPresent = false;
        if (trailerInfoErrors && trailerInfoErrors?.length > 0) {
            trailerInfoErrors.forEach((errorObj) => {
                if (
                    !!errorObj?.registrationNumber ||
                    !!errorObj?.chassisNumber
                ) {
                    isTrailerInfoErrorPresent = true;
                }
            });
        }
        if (!isTrailerInfoErrorPresent) {
            dispatch({
                type: ACTIONS.DELETE_ERROR,
                payload: {
                    key: 'trailersInfo',
                },
            });
        }
    }, [state.trailerInfoErrors]);

    return {
        setShouldScrollIntoView,
        submitFormData,
        state: state!,
        dispatch,
    };
};
